// components/ChampionshipFinalInterface.js
import React, { useState } from 'react';
import Court from './Court';

const ChampionshipFinalInterface = ({ team1Data, team2Data, onFinalComplete }) => {
  // Championship final state
  const [score, setScore] = useState({ team1: 0, team2: 0 });
  const [currentGameType, setCurrentGameType] = useState('women'); // women, men, mixed1, mixed2
  const [pointsSinceRotation, setPointsSinceRotation] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [winner, setWinner] = useState(null);

  // Game type rotation sequence
  const gameTypeSequence = ['women', 'men', 'mixed1', 'mixed2'];
  const gameTypeLabels = {
    women: "Women's Doubles",
    men: "Men's Doubles", 
    mixed1: "Mixed Doubles (M1+F1)",
    mixed2: "Mixed Doubles (M2+F2)"
  };

  // Get players for current game type
  const getCurrentPlayers = () => {
    const team1Players = {
      women: [team1Data.f1, team1Data.f2],
      men: [team1Data.m1, team1Data.m2],
      mixed1: [team1Data.m1, team1Data.f1],
      mixed2: [team1Data.m2, team1Data.f2]
    };

    const team2Players = {
      women: [team2Data.f1, team2Data.f2],
      men: [team2Data.m1, team2Data.m2],
      mixed1: [team2Data.m1, team2Data.f1],
      mixed2: [team2Data.m2, team2Data.f2]
    };

    return {
      team1: team1Players[currentGameType],
      team2: team2Players[currentGameType]
    };
  };

  // Convert team player string to display name
  const getPlayerName = (playerString) => {
    if (!playerString) return '';
    const fullName = playerString.split('(')[0].trim();
    return fullName.split(' ')[0]; // Use first name only
  };

  // Handle score submission
  const handleScoreSubmit = (courtId, team1ScoreInput, team2ScoreInput) => {
    // Validate scores
    if (team1ScoreInput === 0 && team2ScoreInput === 0) {
      alert('Please enter valid scores. Games cannot end 0-0.');
      return;
    }

    const pointsToAdd = Math.max(team1ScoreInput, team2ScoreInput);
    const newScore = {
      team1: score.team1 + (team1ScoreInput > team2ScoreInput ? pointsToAdd : 0),
      team2: score.team2 + (team2ScoreInput > team1ScoreInput ? pointsToAdd : 0)
    };

    const newPointsSinceRotation = pointsSinceRotation + pointsToAdd;

    // Check if game is complete (first to 21, win by 2)
    const isGameComplete = (newScore.team1 >= 21 || newScore.team2 >= 21) && 
                          Math.abs(newScore.team1 - newScore.team2) >= 2;

    if (isGameComplete) {
      const gameWinner = newScore.team1 > newScore.team2 ? 1 : 2;
      setWinner(gameWinner);
      setIsComplete(true);
      setScore(newScore);
      
      // Call completion handler with results
      onFinalComplete({
        winner: gameWinner,
        winnerName: gameWinner === 1 ? team1Data.name : team2Data.name,
        finalScore: newScore,
        gameType: currentGameType,
        gameTypeLabel: gameTypeLabels[currentGameType]
      });
    } else {
      setScore(newScore);
      setPointsSinceRotation(newPointsSinceRotation);

      // Check if we need to rotate players (every 5 points)
      if (newPointsSinceRotation >= 5) {
        const currentIndex = gameTypeSequence.indexOf(currentGameType);
        const nextIndex = (currentIndex + 1) % gameTypeSequence.length;
        setCurrentGameType(gameTypeSequence[nextIndex]);
        setPointsSinceRotation(0);
      }
    }
  };

  const currentPlayers = getCurrentPlayers();
  const pointsUntilRotation = 5 - pointsSinceRotation;

  // Create court data for display
  const courtData = {
    id: 1,
    players: [
      ...currentPlayers.team1.map(getPlayerName),
      ...currentPlayers.team2.map(getPlayerName)
    ],
    isActive: !isComplete,
    timer: { startTime: Date.now(), isActive: !isComplete, duration: 30 * 60 },
    currentGame: {
      id: 'championship-final',
      team1: team1Data.name,
      team2: team2Data.name,
      gameType: currentGameType,
      gameTypeLabel: gameTypeLabels[currentGameType],
      players: {
        team1: currentPlayers.team1.map(getPlayerName),
        team2: currentPlayers.team2.map(getPlayerName)
      },
      isChampionshipFinal: true,
      targetScore: 21
    }
  };

  return (
    <div className="championship-final-interface">
      <div className="championship-header">
        <h1>🏆 Championship Final 🏆</h1>
        <div className="teams-display">
          <h2>{team1Data.name} vs {team2Data.name}</h2>
        </div>
        <div className="score-display">
          <div className="score">
            <span className="team-score">{score.team1}</span>
            <span className="separator">-</span>
            <span className="team-score">{score.team2}</span>
          </div>
        </div>
        {!isComplete && (
          <div className="game-info">
            <p><strong>Current:</strong> {gameTypeLabels[currentGameType]}</p>
            <p><strong>Points until rotation:</strong> {pointsUntilRotation}</p>
            <p><strong>Target:</strong> First to 21, win by 2</p>
          </div>
        )}
        {isComplete && (
          <div className="winner-display">
            <h2>🎉 {winner === 1 ? team1Data.name : team2Data.name} Wins! 🎉</h2>
            <p>Final Score: {score.team1} - {score.team2}</p>
          </div>
        )}
      </div>

      <div className="court-container">
        <Court
          courtData={courtData}
          onScoreSubmit={handleScoreSubmit}
          allPlayers={[]}
          competitionLevel="championship"
          gameInfo={courtData.currentGame}
          getTeamName={(teamId) => teamId === team1Data.name ? team1Data.name : team2Data.name}
        />
      </div>

      <style jsx>{`
        .championship-final-interface {
          padding: 20px;
          background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
          min-height: 100vh;
          color: white;
        }

        .championship-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .championship-header h1 {
          font-size: 2.5rem;
          margin: 0 0 20px 0;
          color: #deff01;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .teams-display h2 {
          font-size: 1.8rem;
          margin: 0 0 20px 0;
          color: white;
        }

        .score-display {
          margin: 20px 0;
        }

        .score {
          font-size: 3rem;
          font-weight: bold;
          color: #deff01;
          text-shadow: 2px 2px 4px rgba(0,0,0,0.3);
        }

        .team-score {
          display: inline-block;
          min-width: 80px;
        }

        .separator {
          margin: 0 20px;
          color: white;
        }

        .game-info {
          background: rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          padding: 16px;
          margin: 20px auto;
          max-width: 400px;
          backdrop-filter: blur(10px);
        }

        .game-info p {
          margin: 8px 0;
          font-size: 1.1rem;
        }

        .winner-display {
          background: linear-gradient(135deg, #deff01 0%, #bef264 100%);
          color: #1e3a8a;
          border-radius: 20px;
          padding: 30px;
          margin: 20px auto;
          max-width: 500px;
          box-shadow: 0 10px 30px rgba(222, 255, 1, 0.4);
        }

        .winner-display h2 {
          margin: 0 0 10px 0;
          font-size: 2rem;
        }

        .winner-display p {
          margin: 0;
          font-size: 1.2rem;
          font-weight: bold;
        }

        .court-container {
          display: flex;
          justify-content: center;
          align-items: center;
        }
      `}</style>
    </div>
  );
};

export default ChampionshipFinalInterface;